/**
 * The choices the alert sheet holds, and the alert they make.
 *
 * Pure, and deliberately React-free. Every value here is the text a control
 * holds; `newAlertFromDraft` is the one place that text becomes the contract.
 */

import { formatCount } from './format.ts'
import {
  ALERT_DETAILS,
  ALERT_FIGURE_OPS,
  type AlertChannel,
  type AlertDetail,
  type AlertFigureOp,
  type AlertReport,
  type AlertSchedule,
  type AlertTrigger,
  type NewAlert
} from './alert-rules-api.ts'
import { selectedView, type AlertSearch } from './alert-view.ts'

/** The longest label an alert may carry. The endpoint's own limit. */
export const LABEL_MAX_LENGTH = 80

/** The four triggers, in the words the form offers them. */
export const TRIGGER_LABEL: Readonly<Record<AlertTrigger['kind'], string>> = {
  schedule: 'Every time, on a schedule',
  rows: 'When anything matches',
  newRows: 'When something new matches',
  figure: 'When a figure moves'
}

/** How a figure comparison reads in the operator select. */
export const FIGURE_OP_LABEL: Readonly<Record<AlertFigureOp, string>> = {
  gte: 'at least',
  lte: 'at most',
  changePct: 'moves by %'
}

/** How much of the answer the DM carries, in one word. */
export const DETAIL_LABEL: Readonly<Record<AlertDetail, string>> = {
  figures: 'Figures',
  counts: 'Counts',
  link: 'Link'
}

/** What each detail choice puts in the DM. Keyed by the select's text. */
export const DETAIL_HINT: Readonly<Record<string, string>> = {
  figures: 'The headline figures and the top rows.',
  counts: 'How many rows matched, and no money.',
  link: 'One line that the alert fired, and the link.'
} satisfies Record<AlertDetail, string>

/** The send-to select's choices, in the order it offers them. */
export const DELIVERY_CHOICES = ['slack', 'email', 'both'] as const

/** One of {@link DELIVERY_CHOICES}. */
export type DeliveryChoice = (typeof DELIVERY_CHOICES)[number]

const CHOICE_CHANNELS: Readonly<
  Record<DeliveryChoice, readonly AlertChannel[]>
> = {
  slack: ['slack'],
  email: ['email'],
  both: ['slack', 'email']
}

/** How each send-to choice reads. */
export const DELIVERY_LABEL: Readonly<Record<DeliveryChoice, string>> = {
  slack: 'Slack DM',
  email: 'Email',
  both: 'Slack DM and email'
}

/**
 * Where a saved alert goes, in the send-to select's words.
 *
 * @param delivery - The alert's channels, each once.
 * @returns The words, such as `Slack DM and email`.
 */
export function deliveryWords(delivery: readonly AlertChannel[]): string {
  if (delivery.length > 1) {
    return DELIVERY_LABEL.both
  }

  return DELIVERY_LABEL[delivery[0] === 'email' ? 'email' : 'slack']
}

/** The choices the sheet holds before they are a `NewAlert`. */
export interface AlertDraft {
  period: string
  dropped: readonly string[]
  trigger: string
  figure: string
  op: string
  value: string
  every: string
  hour: string
  weekday: string
  detail: string
  label: string
  delivery: string
}

/**
 * The draft a sheet opens on: a daily Slack DM at 08:00 that sends counts.
 *
 * @param report - The registry entry.
 * @param period - The period the select starts on.
 * @returns The draft.
 */
export function newDraft(report: AlertReport, period: string): AlertDraft {
  return {
    period,
    dropped: [],
    trigger: 'schedule',
    figure: report.figures[0]?.id ?? '',
    op: 'gte',
    value: '',
    every: 'day',
    hour: '8',
    weekday: '1',
    detail: 'counts',
    label: '',
    delivery: 'slack'
  }
}

/**
 * Read a whole number a select stored.
 *
 * @param value - The select's value.
 * @param max - The highest value allowed. The lowest is 0.
 * @returns The number, or null when it is not 0 to `max`.
 */
function intIn(value: string, max: number): number | null {
  return /^\d+$/.test(value) && Number(value) <= max ? Number(value) : null
}

/**
 * Read one of a fixed list of choices a select stored.
 *
 * @param value - The select's value.
 * @param list - The choices allowed.
 * @returns The choice, or null when the list does not hold it.
 */
function oneOf<T extends string>(value: string, list: readonly T[]): T | null {
  return list.find(item => item === value) ?? null
}

/**
 * Say why a label cannot be stored, or null when it can.
 *
 * A blank label is no label. Only a label past the limit is a problem.
 *
 * @param label - What is in the label box.
 * @returns The problem, or null.
 */
export function labelProblem(label: string): string | null {
  return label.trim().length > LABEL_MAX_LENGTH
    ? `A label holds at most ${formatCount(LABEL_MAX_LENGTH)} characters.`
    : null
}

/**
 * Say why a figure value cannot be stored, or null when it can.
 *
 * `changePct` must be above zero. The other operators take any finite number.
 *
 * @param op - The operator the reader picked.
 * @param value - What is in the value box.
 * @returns The problem, or null.
 */
export function figureValueProblem(op: string, value: string): string | null {
  const text = value.trim()
  if (text === '' || !Number.isFinite(Number(text))) {
    return 'Enter a number.'
  }
  if (op === 'changePct' && !(Number(text) > 0)) {
    return 'Enter a number above 0.'
  }

  return null
}

/**
 * An hour as `HH:00`.
 *
 * @param hour - The hour, 0–23.
 * @returns The label.
 */
export function formatAlertHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`
}

/**
 * The trigger, or null when the report does not offer it or the figure
 * choice is incomplete.
 *
 * @param draft - The sheet's choices.
 * @param report - The registry entry.
 * @returns The trigger.
 */
export function triggerFromDraft(
  draft: AlertDraft,
  report: AlertReport
): AlertTrigger | null {
  const kind = oneOf(draft.trigger, report.triggers)
  if (kind !== 'figure') {
    return kind === null ? null : { kind }
  }
  const op = oneOf(draft.op, ALERT_FIGURE_OPS)
  if (
    !op ||
    (op === 'changePct' && !report.takesPeriod) ||
    figureValueProblem(op, draft.value) !== null ||
    !report.figures.some(figure => figure.id === draft.figure)
  ) {
    return null
  }

  return { kind, figure: draft.figure, op, value: Number(draft.value.trim()) }
}

/**
 * The schedule, or null when this report refuses an hourly check.
 *
 * @param draft - The sheet's choices.
 * @param hourly - Whether the report allows an hourly check.
 * @returns The schedule.
 */
function scheduleFromDraft(
  draft: AlertDraft,
  hourly: boolean
): AlertSchedule | null {
  if (draft.every === 'hour') {
    return hourly ? { every: 'hour' } : null
  }
  const hour = intIn(draft.hour, 23)
  if (hour === null) {
    return null
  }
  if (draft.every === 'day') {
    return { every: 'day', hour }
  }
  const weekday = intIn(draft.weekday, 6)

  return draft.every === 'week' && weekday !== null
    ? { every: 'week', weekday, hour }
    : null
}

/**
 * The alert the sheet would save, or null when the draft is not valid yet.
 *
 * @param draft - The sheet's choices.
 * @param report - The registry entry.
 * @param search - The page's URL search.
 * @returns The body `createAlert` and `previewAlert` post.
 */
export function newAlertFromDraft(
  draft: AlertDraft,
  report: AlertReport,
  search: AlertSearch
): NewAlert | null {
  const view = selectedView(search, report, draft.period, draft.dropped)
  const trigger = triggerFromDraft(draft, report)
  const schedule = scheduleFromDraft(draft, report.hourly)
  const detail = oneOf(draft.detail, ALERT_DETAILS)
  const delivery = oneOf(draft.delivery, DELIVERY_CHOICES)
  if (
    !view ||
    !trigger ||
    !schedule ||
    !detail ||
    !delivery ||
    labelProblem(draft.label) !== null
  ) {
    return null
  }
  const label = draft.label.trim()

  return {
    source: { kind: 'report', report: report.id, view },
    trigger,
    schedule,
    detail,
    label: label === '' ? null : label,
    delivery: [...CHOICE_CHANNELS[delivery]]
  }
}
